import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { QrCode, ArrowLeft, Mail, Lock, Loader2, Shield, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { signIn, getIdToken } from "../lib/firebase";

const API = process.env.REACT_APP_BACKEND_URL;

const AdminLoginPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error("Please enter email and password");
      return;
    }

    setLoading(true);
    try {
      // Firebase login
      await signIn(email, password);
      
      // Get Firebase ID token
      const token = await getIdToken();
      
      // Check if user is admin
      const response = await fetch(`${API}/api/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const userData = await response.json();
      
      if (!userData.registered) {
        toast.error("Account not found. Please contact system administrator.");
        return;
      }
      
      if (userData.role !== 'admin') {
        toast.error("Access denied. This login is for administrators only.");
        return;
      }
      
      toast.success("Admin login successful!");
      navigate("/admin", { replace: true });
    } catch (error) {
      console.error("Admin login error:", error);
      if (error.code === 'auth/user-not-found') {
        toast.error("Admin account not found");
      } else if (error.code === 'auth/wrong-password') {
        toast.error("Incorrect password");
      } else if (error.code === 'auth/invalid-email') {
        toast.error("Invalid email address");
      } else if (error.code === 'auth/too-many-requests') {
        toast.error("Too many attempts. Please try again later.");
      } else {
        toast.error(error.message || "Login failed");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] p-6 flex flex-col">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/")}
          data-testid="back-btn"
          className="text-[#475569] hover:bg-[#F1F5F9]"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex items-center gap-2">
          <QrCode className="w-8 h-8 text-[#2563EB]" />
          <span className="text-xl font-bold text-[#0F172A]" style={{ fontFamily: 'Manrope' }}>
            QuickQRID
          </span>
        </div>
      </div>

      {/* Admin Login Card */}
      <Card className="flex-1 bg-white border-[#E2E8F0] shadow-lg max-w-md mx-auto w-full">
        <CardHeader className="text-center pb-2">
          <div className="w-16 h-16 bg-[#2563EB]/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-[#2563EB]" />
          </div>
          <CardTitle className="text-2xl font-bold text-[#0F172A]" style={{ fontFamily: 'Manrope' }}>
            Admin Login
          </CardTitle>
          <CardDescription className="text-[#475569]">
            Authorized personnel only
          </CardDescription>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleAdminLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="admin-email" className="text-[#0F172A] font-medium">
                Admin Email
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94A3B8]" />
                <Input
                  id="admin-email"
                  type="email"
                  placeholder="Enter admin email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 h-12 bg-white border-[#E2E8F0] text-[#0F172A] placeholder:text-[#94A3B8]"
                  data-testid="admin-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="admin-password" className="text-[#0F172A] font-medium">
                Password
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94A3B8]" />
                <Input
                  id="admin-password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-10 h-12 bg-white border-[#E2E8F0] text-[#0F172A] placeholder:text-[#94A3B8]"
                  data-testid="admin-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#475569]"
                  data-testid="admin-password-toggle"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-medium"
              data-testid="admin-login-btn"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
              ) : (
                <Shield className="w-5 h-5 mr-2" />
              )}
              Sign In as Admin
            </Button>
          </form>

          <div className="mt-6 pt-4 border-t border-[#E2E8F0]">
            <p className="text-center text-sm text-[#475569]">
              Not an admin?{" "}
              <button
                onClick={() => navigate("/login")}
                className="text-[#2563EB] hover:underline"
              >
                Go to User Login
              </button>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminLoginPage;
